import { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const LoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pinLogin, user, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [pin, setPin] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef([]);

  // Clear any existing session when visiting login page
  useEffect(() => {
    logout();
  }, []);

  const handlePinChange = (index, value) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return;

    const newPin = [...pin];
    newPin[index] = value;
    setPin(newPin);

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all 6 digits are entered
    if (value && index === 5) {
      const fullPin = newPin.join('');
      if (fullPin.length === 6) {
        handleSubmit(fullPin);
      }
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (/^\d+$/.test(pastedData)) {
      const newPin = pastedData.split('').concat(Array(6).fill('')).slice(0, 6);
      setPin(newPin);
      if (pastedData.length === 6) {
        handleSubmit(pastedData);
      } else {
        inputRefs.current[pastedData.length]?.focus();
      }
    }
  };

  const handleSubmit = async (pinValue) => {
    const fullPin = pinValue || pin.join('');
    if (fullPin.length !== 6) {
      toast.error(t('login.pinRequired'));
      return;
    }

    setLoading(true);
    try {
      await pinLogin(fullPin);
      toast.success(t('login.welcome'));
      navigate('/admin');
    } catch (error) {
      const message = error.response?.data?.detail || t('login.invalidPin');
      toast.error(message);
      // Clear PIN on error
      setPin(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  if (user) return null;

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="login-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-4">
            <img 
              src="https://res.cloudinary.com/dtqyyb6su/image/upload/v1774460834/entrepot/assets/company_logo_footer.png" 
              alt="Entrepôt Auto Montréal" 
              className="h-12 w-auto"
            />
            <span className="font-bold text-xl text-white" style={{ fontFamily: "'Montserrat', sans-serif" }}>Entrepôt Auto Montréal</span>
          </Link>
          <h1 className="text-2xl font-bold text-white">
            {t('login.title')}
          </h1>
          <p className="text-slate-400 mt-2">
            {t('login.subtitle')}
          </p>
        </div>

        {/* PIN Form */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-8">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 rounded-full bg-blue-600/20 flex items-center justify-center">
              <Lock className="h-8 w-8 text-blue-500" />
            </div>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="space-y-6" data-testid="login-form">
            {/* PIN Input Grid */}
            <div className="flex justify-center gap-2">
              {pin.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handlePinChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  className="w-12 h-14 text-center text-2xl font-bold bg-slate-800 border-2 border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none transition-colors"
                  data-testid={`pin-input-${index}`}
                  autoFocus={index === 0}
                />
              ))}
            </div>

            <Button 
              type="submit" 
              className="w-full bg-blue-600 hover:bg-blue-700 text-lg py-6"
              disabled={loading || pin.join('').length !== 6}
              data-testid="login-submit"
            >
              {loading ? t('login.verifying') : t('login.button')}
            </Button>
          </form>

          <p className="text-center text-slate-500 text-sm mt-4">
            {t('login.forgotPin')}
          </p>
        </div>

        {/* Back Link */}
        <div className="text-center mt-6">
          <Link to="/" className="text-sm text-slate-400 hover:text-blue-500 transition-colors">
            ← {t('login.backToSite')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
